import { useEffect } from 'react';
import { useUIStore } from '@/stores/use-ui-store';
import { isDesktop as isDesktopDefault } from '@/lib/platform';

export interface GlobalShortcutOptions {
  /** Whether the app is running inside Tauri desktop (injected for testability). */
  isDesktop?: boolean;
  /** Hides the native window (injected for testability; defaults to Tauri getCurrentWindow().hide()). */
  hideWindow?: () => void;
  /**
   * Closes the currently active tab. When undefined (HOME — no active tab),
   * ⌘W / Ctrl+W does nothing. Injected for testability; in production wired
   * to closeTab() from useSessionState.
   */
  closeActiveTab?: () => void;
  /**
   * Creates a new document and navigates to it. ⌘T / Ctrl+T on all platforms.
   * Injected for testability; in production wired to the document service.
   */
  createNewDocument?: () => void;
  /**
   * Creates a new canvas and navigates to it. ⌘⇧T / Ctrl+Shift+T on all platforms.
   * Injected for testability; in production wired to the document service.
   */
  createNewCanvas?: () => void;
  /** Navigate to the previous tab (⌘[ / Ctrl+[). No-op when undefined. */
  navigateToPrevTab?: () => void;
  /** Navigate to the next tab (⌘] / Ctrl+]). No-op when undefined. */
  navigateToNextTab?: () => void;
}

/**
 * Registers global keyboard shortcuts. Mount once near the root of the app.
 *
 * Shortcuts:
 *  Cmd/Ctrl+K  — open search overlay
 *  Escape      — close search overlay; hide window (desktop, no overlay open)
 *  Cmd/Ctrl+T        — new document (all platforms)
 *  Cmd/Ctrl+Shift+T  — new canvas (all platforms)
 *  Cmd/Ctrl+W  — close active tab; no-op on HOME (desktop only; on macOS ⌘W
 *                is swallowed by WKWebView so this fires only on Windows)
 *  Cmd/Ctrl+[  — navigate to previous tab (wraps around)
 *  Cmd/Ctrl+]  — navigate to next tab (wraps around)
 *  Cmd/Ctrl+,  — open Settings (on macOS ⌘, fires through the native menu;
 *                this JS branch handles Windows Ctrl+,)
 *  Cmd/Ctrl+\  — toggle Focus (zen) mode
 *  Cmd/Ctrl+J  — toggle AI assistant panel
 *  T           — calendar: go to today    (fires custom event "calendar:nav")
 *  J           — calendar: go to previous period
 *  K           — calendar: go to next period
 *  ← / →       — calendar: previous / next period (when not in an input)
 *
 * Note: ⌘H (Hide App) is intentionally not handled here — on macOS NSApp
 * intercepts it before JS sees it, and on Windows Ctrl+H is Find & Replace.
 * Hide App is available via the macOS menu bar item.
 */
export function useGlobalShortcut(options: GlobalShortcutOptions = {}): void {
  const {
    isDesktop = isDesktopDefault,
    hideWindow = defaultHideWindow,
    closeActiveTab,
    createNewDocument,
    createNewCanvas,
    navigateToPrevTab,
    navigateToNextTab,
  } = options;

  const setSearchOpen = useUIStore((s) => s.setSearchOpen);
  const isSearchOpen = useUIStore((s) => s.isSearchOpen);
  const assistantMode = useUIStore((s) => s.assistantMode);
  const setAssistantMode = useUIStore((s) => s.setAssistantMode);
  const isZenMode = useUIStore((s) => s.isZenMode);
  const setZenMode = useUIStore((s) => s.setZenMode);
  const setSettingsOpen = useUIStore((s) => s.setSettingsOpen);
  const isSettingsOpen = useUIStore((s) => s.isSettingsOpen);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // ── Search ──────────────────────────────────────────────────────────
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setSearchOpen(true);
        return;
      }

      // ── Escape: close overlays, then hide window ─────────────────────────
      if (e.key === 'Escape') {
        if (isSearchOpen) {
          setSearchOpen(false);
          return;
        }
        // No overlay open — hide the window on desktop
        if (isDesktop && !isSettingsOpen) {
          hideWindow();
        }
        return;
      }

      // ── Settings ⌘, / Ctrl+, ────────────────────────────────────────────
      if (e.key === ',' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setSettingsOpen(true);
        return;
      }

      // ── ⌘W / Ctrl+W — close active tab, do nothing on HOME ─────────────
      // On macOS ⌘W is swallowed by WKWebView so this branch only fires on
      // Windows (Ctrl+W). When a doc tab is active, close it. When on HOME
      // (no active tab / closeActiveTab not provided), do nothing — hiding the
      // window on ⌘W when no tab is open is unexpected and jarring.
      if (isDesktop && e.key === 'w' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        closeActiveTab?.();
        return;
      }

      // ── New canvas ⌘⇧T / Ctrl+Shift+T (checked before ⌘T to avoid overlap)
      if (e.key === 't' && (e.metaKey || e.ctrlKey) && e.shiftKey) {
        e.preventDefault();
        createNewCanvas?.();
        return;
      }

      // ── New document ⌘T / Ctrl+T ────────────────────────────────────────
      if (e.key === 't' && (e.metaKey || e.ctrlKey) && !e.shiftKey) {
        e.preventDefault();
        createNewDocument?.();
        return;
      }

      // ── Tab navigation ⌘[ / ⌘] ──────────────────────────────────────────
      if (e.key === '[' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        navigateToPrevTab?.();
        return;
      }
      if (e.key === ']' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        navigateToNextTab?.();
        return;
      }

      // ── Zen mode ⌘\ ─────────────────────────────────────────────────────
      if (e.key === '\\' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setZenMode(!isZenMode);
        return;
      }

      // ── Assistant ⌘J ────────────────────────────────────────────────────
      if (e.key === 'j' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (assistantMode === 'hidden') {
          setAssistantMode('fullTab');
        } else if (assistantMode === 'collapsed') {
          setAssistantMode('expanded');
        } else if (assistantMode === 'expanded') {
          setAssistantMode('collapsed');
        } else if (assistantMode === 'fullTab') {
          setAssistantMode('collapsed');
        }
        // 'sidebar' mode: ⌘J does nothing — sidebar has its own toggle
        return;
      }

      // ── Calendar shortcuts — skip when typing in an input/textarea ──────
      const target = e.target as HTMLElement;
      const isTyping =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;
      if (isTyping || e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === 't' || e.key === 'T') {
        window.dispatchEvent(new CustomEvent('calendar:nav', { detail: 'today' }));
      } else if (e.key === 'j' || e.key === 'J' || e.key === 'ArrowLeft') {
        window.dispatchEvent(new CustomEvent('calendar:nav', { detail: 'prev' }));
      } else if (e.key === 'k' || e.key === 'K' || e.key === 'ArrowRight') {
        window.dispatchEvent(new CustomEvent('calendar:nav', { detail: 'next' }));
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    setSearchOpen, isSearchOpen, isSettingsOpen, setSettingsOpen,
    assistantMode, setAssistantMode,
    isZenMode, setZenMode,
    isDesktop, hideWindow, closeActiveTab, createNewDocument, createNewCanvas,
    navigateToPrevTab, navigateToNextTab,
  ]);
}

function defaultHideWindow(): void {
  import('@tauri-apps/api/window')
    .then(({ getCurrentWindow }) => getCurrentWindow().hide())
    .catch(() => {/* no-op in web context */});
}
